import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';

const AuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    // Prevent multiple executions (React Strict Mode, re-renders, etc.)
    if (hasProcessed.current) {
      return;
    }

    const handleAuth = async () => {
      try {
        setLoading(true);
        hasProcessed.current = true;
        
        // Log for debugging
        console.log('AuthCallback: Processing authentication...', {
          searchParams: Object.fromEntries(searchParams.entries()),
          url: window.location.href,
        });
        
        // Check for error in URL
        const errorParam = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        const errorCode = searchParams.get('error_code');
        
        if (errorParam) {
          const errorMessage = errorParam === 'access_denied' 
            ? 'Access denied. Please authorize the application.' 
            : errorDescription || errorParam;
          console.error('AuthCallback: Error from backend:', { errorParam, errorDescription, errorCode });
          setError(errorMessage);
          setLoading(false);
          return;
        }

        // Backend redirects with JWT token in query params
        const token = searchParams.get('token');
        
        if (!token) {
          // Check if we're on the callback page but no token - might be a direct visit
          console.warn('AuthCallback: No token found in URL. Possible causes:', {
            url: window.location.href,
            searchParams: Object.fromEntries(searchParams.entries()),
            referrer: document.referrer,
          });
          
          // If we have a referrer from Discord, this might be a backend issue
          if (document.referrer.includes('discord.com')) {
            setError('Authentication failed: No token received from server. Please try logging in again.');
          } else {
            // Direct visit to callback page without going through Discord
            setError('No authentication token found. Please start by clicking the Login button.');
          }
          setLoading(false);
          return;
        }
        
        // Clear the token from URL for security
        window.history.replaceState(null, '', window.location.pathname);
        
        console.log('AuthCallback: Token received, attempting login...');
        
        // Login with the token
        await login(token);
        
        console.log('AuthCallback: Login successful, navigating to dashboard...');
        
        // Navigate to dashboard
        navigate('/dashboard', { replace: true });
      } catch (err) {
        console.error('AuthCallback: Login error:', err);
        const errorMessage = err instanceof Error ? err.message : 'Authentication failed';
        setError(errorMessage);
        setLoading(false);
        hasProcessed.current = false; // Allow retry on error
      }
    };

    handleAuth();
  }, [searchParams, login, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Authenticating...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md mx-4">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold mb-2">Authentication Failed</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <div className="flex gap-4 justify-center">
            <Button onClick={() => navigate('/')} variant="outline">
              Return to Home
            </Button>
            <Button 
              onClick={async () => {
                try {
                  const { authService } = await import('@/lib/auth');
                  await authService.signInWithDiscord();
                } catch (err) {
                  console.error('Failed to initiate login:', err);
                  setError('Failed to start login process. Please try again.');
                }
              }}
            >
              Try Again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default AuthCallback;
